import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DOMPurify from "dompurify";
import MDButton from "../../../../components/MDButton";
import { get, post } from "../../../../requests";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import SendIcon from "@mui/icons-material/Send";

// ----------------------
// Types
// ----------------------

interface EmailAddress {
  name: string;
  address: string;
}

interface Recipient {
  emailAddress: EmailAddress;
}

interface EmailResponse {
  status: "success" | "error";
  submissionContent: {
    data: any[];
  };
}

interface EmailTabProps {
  applicationId: number;
  isReadOnly: boolean;
}

// ----------------------
// Component
// ----------------------

const EmailTabComponent: React.FC<EmailTabProps> = ({ applicationId, isReadOnly }) => {
  const [conversations, setConversations] = useState<any[]>([]);
  const [value, setValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showReplyBox, setShowReplyBox] = useState<boolean>(false);
  const [hoverReplyAll, setHoverReplyAll] = useState(false);
  const [hoverSend, setHoverSend] = useState(false);

  useEffect(() => {
    loadEmailData();
  }, []);

  const loadEmailData = () => {
    setLoading(true);
    get(`dealApplication/getEmailData/${applicationId}`)
      .then((response: EmailResponse) => {
        if (response.status === "success") {
          setConversations(response.submissionContent?.data);
        }
        setLoading(false);
      })
      .catch((err: any) => {
        console.error(err);
        setLoading(false);
      });
  };

  const sendEmail = async () => {
    if (!value.trim()) return;

    setLoading(true);
    try {
      const emailData = {
        content: {
          customEmail: {
            content: value,
          },
        },
        applicationId,
      };

      const response = await post("dealApplication/sendEmailToSubmissionSys", emailData);
      if (response) {
        setValue("");
        setShowReplyBox(false);
        loadEmailData();
      }
    } finally {
      setLoading(false);
    }
  };

  const sanitizeHTML = (html: string) => DOMPurify.sanitize(html);

  return (
    <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
      <Box style={{ marginLeft: "25px", color: "#A6A6A6" }}>
        {loading && (
          <Box style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && conversations.length > 0 ? (
          <Paper elevation={3} style={{ padding: "16px", marginRight: "10px", marginTop: "10px" }}>
            {showReplyBox ? (
              <Box style={{ paddingBottom: "10px", marginBottom: "10px" }}>
                <ReactQuill
                  theme="snow"
                  value={value}
                  onChange={setValue}
                  style={{ color: "darkslategray" }}
                />
                <Box
                  onMouseEnter={() => setHoverSend(true)}
                  onMouseLeave={() => setHoverSend(false)}
                  style={{ display: "inline-block" }}
                >
                  <MDButton
                    onClick={sendEmail}
                    style={{
                      border: "none",
                      backgroundColor: hoverSend ? "#2f929f" : "white",
                      color: hoverSend ? "white" : "#2f929f",
                      marginTop: "10px",
                      marginRight: "10px",
                    }}
                    startIcon={<SendIcon style={{ color: hoverSend ? "white" : "#2f929f" }} />}
                  >
                    Send
                  </MDButton>
                </Box>
              </Box>
            ) : (
              <Box
                onMouseEnter={() => setHoverReplyAll(true)}
                onMouseLeave={() => setHoverReplyAll(false)}
                style={{ display: "inline-block" }}
              >
                <MDButton
                  onClick={() => setShowReplyBox(true)}
                  style={{
                    border: "none",
                    backgroundColor: hoverReplyAll ? "#2f929f" : "white",
                    color: hoverReplyAll ? "white" : "#2f929f",
                    marginTop: "10px",
                  }}
                  startIcon={
                    <ReplyAllIcon style={{ color: hoverReplyAll ? "white" : "#2f929f" }} />
                  }
                >
                  Reply All
                </MDButton>
              </Box>
            )}

            <Typography variant="subtitle1" style={{ marginBottom: "10px" }}>
              <strong>Subject:</strong> {conversations[0]?.subject || "N/A"}
            </Typography>

            {conversations.map((conv, index) => (
              <Box key={index} style={{ marginBottom: "15px" }}>
                <Typography variant="body2">
                  <strong>From:</strong> {conv.sender.name} &lt;{conv.sender.emailAddress}&gt;
                </Typography>
                <Typography variant="body2">
                  <strong>To:</strong>{" "}
                  {conv?.toRecipients.map((r: any) => `${r.name} <${r.address}>`).join(", ")}
                </Typography>
                {conv?.ccRecipients?.length > 0 && (
                  <Typography variant="body2">
                    <strong>Cc:</strong>{" "}
                    {conv.ccRecipients.map((r: any) => `${r?.name} <${r?.address}>`).join(", ")}
                  </Typography>
                )}
                <Box
                  style={{
                    color: "black",
                    borderBottom: "1px solid #A6A6A6",
                    paddingBottom: "10px",
                    marginTop: "10px",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(conv.body),
                  }}
                />
              </Box>
            ))}
          </Paper>
        ) : (
          !loading && (
            <Typography variant="body1" style={{ marginTop: "10px" }}>
              No Email Thread Is Associated With This Application.
            </Typography>
          )
        )}
      </Box>
    </div>
  );
};

export default EmailTabComponent;
